import { Badge, Status } from '@nexelem/components';
import { CUSTOM_ICONS } from '@nexelem/icons';
import type { TaskToDoEntry } from '@nexelem/graphne-api';
import { useDateFormatter } from '@nexelem/utils';
import type { SyntheticEvent } from 'react';
import { useCustomTranslation } from '../../../../hooks';
import { getTaskStatusColor } from '../utils';
import { useIsGanttSimulation } from '../../ProductionPlans/GanttSimulation/hooks/GanttSimulationContext';
import { useFlatTasksListStore } from '../store/useFlatTasksListStore';
import { ToDoCheckbox } from './ToDoCheckbox';
import './FlatListBatchHeader.styles.css';

export type FlatListBatchHeaderProps = {
  batchId: number;
  tasks: TaskToDoEntry[];
  onCheck: (taskId: string) => void;
};

export const FlatListBatchHeader = ({ batchId, tasks, onCheck }: FlatListBatchHeaderProps) => {
  const isSimulation = useIsGanttSimulation();
  const { t, tWrapper } = useCustomTranslation('products.productionPlan.todoList');
  const dateFormatter = useDateFormatter({ displayWithHours: true });
  const flatTaskListCheckMap = useFlatTasksListStore((state) => state.flatTaskListCheckMap);

  const firstTask = tasks[0];
  const statusColor = getTaskStatusColor(firstTask?.status) || 'grey';
  const checkedCount = tasks.filter((task) => flatTaskListCheckMap.get(task.id.toString())?.checked).length;
  const isChecked = checkedCount === tasks.length;
  const isIntermediate = checkedCount > 0 && !isChecked;

  const handleBatchCheck = (evt: SyntheticEvent) => {
    evt.stopPropagation();
    evt.preventDefault();
    const targetChecked = !isChecked;
    tasks.forEach((task) => {
      const taskId = task.id.toString();
      const currentlyChecked = flatTaskListCheckMap.get(taskId)?.checked || false;
      if (currentlyChecked !== targetChecked) {
        onCheck(taskId);
      }
    });
  };

  return (
    <div className="cursor-pointer border-b border-border-secondary transition-colors hover:bg-surface-focus-lighter">
      <div className="flex items-center gap-0.5 py-3 pr-1 pl-2">
        {isSimulation && (
          <div className="flex items-center justify-center">
            <ToDoCheckbox checked={isChecked} isIntermediate={isIntermediate} onChange={handleBatchCheck} />
          </div>
        )}
        <Status className="vertical-dash text-s16" color={statusColor} Icon={CUSTOM_ICONS.Merge} variant="icon" />
        <div className="ml-3 flex flex-col gap-1 text-s11-sb uppercase">
          <div className="flex items-center gap-2">
            <Badge className="bg-surface-primary">{tasks.length}</Badge>
            <div className="flex gap-2">
              <span>{t('products.productionPlan.batchId')}</span>
              <span>{batchId}</span>
            </div>
          </div>
          <div className="flex gap-1">
            <span>{tWrapper('dueDate')}</span>
            <span>{dateFormatter(firstTask.dueDate) || '-'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
